import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import {
    ConfirmationDialogVersions,
    ConfirmationDialog,
} from '../operator-logins/components/ConfirmationDialog.tsx';
import { Box, TextField, Stack } from '@mui/material';
import { useState } from 'react';
import { PasswordChangeTokenSource } from './ChangePasswordController.tsx';

export function ChangePasswordForm({
    source,
    onSave,
    onCancel,
}: {
    source: PasswordChangeTokenSource;
    onSave: (password: string) => void;
    onCancel: () => void;
}) {
    const [state, setState] = useState<State>({
        password: '',
        passwordEdited: false,
        confirmation: '',
    });

    return (
        <ConfirmationDialog
            title={
                <Localized
                    de="Passwort zurücksetzen"
                    fr="Réinitialiser le mot de passe"
                    it="Reimposta password"
                    en="Reset password"
                />
            }
            open={true}
            fullWidth={false}
            dialogVersion={ConfirmationDialogVersions.PRIMARY}
            confirmLabel={
                <Localized
                    de="Speichern"
                    fr="Sauvegarder"
                    it="Salva"
                    en="Save"
                />
            }
            onConfirm={() => onSave(state.password)}
            confirmDisabled={
                validate(state.password, state.confirmation) !==
                ValidationResult.OK
            }
            closeDisabled={true}
            onCancel={
                source === PasswordChangeTokenSource.WEB ? onCancel : undefined
            }
        >
            <Box sx={{ marginY: 3, width: '300px' }}>
                <form
                    onSubmit={event => {
                        event.preventDefault();
                        handleEnter(onSave, state);
                    }}
                >
                    <Stack spacing={3}>
                        <TextField
                            value={state.password}
                            label={
                                <Localized
                                    de="Neues Passwort"
                                    fr="Nouveau mot de passe"
                                    it="Nuova password"
                                    en="New password"
                                />
                            }
                            onBlur={() =>
                                setState(state => ({
                                    ...state,
                                    passwordEdited: true,
                                }))
                            }
                            type="password"
                            error={passwordErrorText(state) !== null}
                            helperText={
                                validate(state.password, state.confirmation) !==
                                ValidationResult.OK
                                    ? passwordErrorText(state)
                                    : ''
                            }
                            fullWidth
                            onChange={event =>
                                setState(state => ({
                                    ...state,
                                    password: event.target.value,
                                }))
                            }
                        />

                        <TextField
                            value={state.confirmation}
                            label={
                                <Localized
                                    de="Passwort bestätigen"
                                    fr="Confirmer le mot de passe"
                                    it="Conferma password"
                                    en="Confirm password"
                                />
                            }
                            type="password"
                            error={confirmationErrorText(state) !== null}
                            helperText={
                                validate(state.password, state.confirmation) !==
                                ValidationResult.OK
                                    ? confirmationErrorText(state)
                                    : ''
                            }
                            fullWidth
                            onChange={event =>
                                setState(state => ({
                                    ...state,
                                    confirmation: event.target.value,
                                }))
                            }
                        />

                        <button
                            type="submit"
                            hidden
                            disabled={
                                validate(state.password, state.confirmation) !==
                                ValidationResult.OK
                            }
                        />
                    </Stack>
                </form>
            </Box>
        </ConfirmationDialog>
    );
}

export interface State {
    password: string;
    passwordEdited: boolean;
    confirmation: string;
}

function handleEnter(onSave: (password: string) => void, state: State): void {
    if (validate(state.password, state.confirmation) === ValidationResult.OK) {
        onSave(state.password);
    }
}

function passwordErrorText(state: State) {
    if (!state.passwordEdited) {
        return null;
    }

    switch (validate(state.password, state.confirmation)) {
        case ValidationResult.ERROR_EMPTY:
            return (
                <Localized
                    de="Pflichtfeld"
                    fr="Champ obligatoire"
                    it="Campo obbligatorio"
                    en="Required field"
                />
            );

        case ValidationResult.ERROR_TOO_SHORT:
            return (
                <Localized
                    de={`Das Passwort ist zu kurz. Es muss midestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`}
                    fr={`Le mot de passe est trop court. Il doit contenir au moins ${MIN_PASSWORD_LENGTH} caractères.`}
                    it={`La password è troppo corta. Deve contenere almeno ${MIN_PASSWORD_LENGTH} caratteri.`}
                    en={`The password is too short. It must be at least ${MIN_PASSWORD_LENGTH} characters long.`}
                />
            );

        default:
            return null;
    }
}

function confirmationErrorText(state: State) {
    if (
        !state.passwordEdited ||
        state.confirmation.length < state.password.length
    ) {
        return null;
    }

    if (
        validate(state.password, state.confirmation) ===
        ValidationResult.ERROR_CONFIRMATION_MISMATCH
    ) {
        return (
            <Localized
                de="Passwörter stimmen nicht überein."
                fr="Les mots de passe ne correspondent pas."
                it="Le due password inserite non corrispondono."
                en="The passwords do not match."
            />
        );
    }

    return null;
}

function validate(password: string, confirmation: string): ValidationResult {
    if (password === '') {
        return ValidationResult.ERROR_EMPTY;
    }

    if (password.length < MIN_PASSWORD_LENGTH) {
        return ValidationResult.ERROR_TOO_SHORT;
    }

    if (password !== confirmation) {
        return ValidationResult.ERROR_CONFIRMATION_MISMATCH;
    }

    return ValidationResult.OK;
}

enum ValidationResult {
    OK,
    ERROR_EMPTY,
    ERROR_TOO_SHORT,
    ERROR_CONFIRMATION_MISMATCH,
}

export const MIN_PASSWORD_LENGTH = 5;
